import { useCallback, useMemo } from "react"
import { Typography, Stack, Divider, Tooltip } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
import { useAppLocalization } from "../../../modules/localization/components/AppLocalizationProvider"
import { tailwindPalette } from "../../../modules/theme/tailwindPalette"
import {
    B_PACK_EXTENSION_URL,
    B_PACK_EXTENSION_URL_EDGE,
} from "../../../modules/serviceCenter/report/external/brotherPrinter"
import { StandardDialog } from "./StandardDialog"
import { ExternalLink, ExternalLinkButton } from "../links/ExternalLink"
import { StandardButton } from "../buttons/StandardButton"
import { TranslationKey } from "../../../modules/localization/translations"

export enum LabelPrinterDialogVariant {
    NOT_INSTALLED = "NOT_INSTALLED",
    PRINTER_NOT_FOUND = "PRINTER_NOT_FOUND",
}

type LabelPrinterExtensionDialogProps = Readonly<{
    open: boolean
    onClose: () => void
    variant: LabelPrinterDialogVariant
}>

type BrowserExtensionConfig = Readonly<{
    extensionUrl: string
    buttonId: TranslationKey
    buttonText: string
}>

const useStyles = makeStyles()(() => ({
    title: {
        fontSize: "1.25rem",
        fontWeight: 600,
        color: tailwindPalette.slate[900],
    },

    description: {
        fontSize: ".875rem",
        color: tailwindPalette.slate[600],
        textAlign: "center",
    },
    helpDivider: {
        width: "100%",
        marginTop: "1rem",
    },
    helpLabel: {
        fontSize: ".8125rem",
        fontWeight: 500,
        color: tailwindPalette.slate[500],
    },
    helpLink: {
        fontSize: ".8125rem",
        color: tailwindPalette.sky[700],
        "&:hover": {
            color: tailwindPalette.sky[500],
        },
    },
    refreshLink: {
        color: tailwindPalette.sky[700],
        cursor: "pointer",
        textDecoration: "underline",
        "&:hover": {
            color: tailwindPalette.sky[500],
        },
    },
    warningIcon: {
        fontSize: "2.5rem",
        color: tailwindPalette.amber[500],
    },
}))

function NotInstalledContent() {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    const browserConfig = useMemo((): BrowserExtensionConfig => {
        const isEdge = /Edg\//.test(navigator.userAgent)
        return isEdge
            ? {
                  extensionUrl: B_PACK_EXTENSION_URL_EDGE,
                  buttonId: "label-printer-dialog-install-edge-button",
                  buttonText: "Install for Edge",
              }
            : {
                  extensionUrl: B_PACK_EXTENSION_URL,
                  buttonId: "label-printer-dialog-install-chrome-button",
                  buttonText: "Install for Chrome",
              }
    }, [])

    const handleReloadPage = useCallback(() => {
        window.location.reload()
    }, [])

    return (
        <Stack alignItems="center" gap="1rem" padding="1rem">
            <Stack direction="row" gap="1rem" justifyContent="center">
                <Tooltip title={l10n.getString("label-printer-dialog-install-tooltip")} arrow>
                    <span>
                        <LocalizedStrict id={browserConfig.buttonId}>
                            <ExternalLinkButton
                                href={browserConfig.extensionUrl}
                                color="black"
                                size="medium"
                                fullWidth={false}
                            >
                                {browserConfig.buttonText}
                            </ExternalLinkButton>
                        </LocalizedStrict>
                    </span>
                </Tooltip>
            </Stack>
            <LocalizedStrict
                id="label-printer-dialog-refresh-after-install"
                elems={{
                    refreshLink: <span className={classes.refreshLink} onClick={handleReloadPage} />,
                }}
            >
                <Typography className={classes.description}>
                    After installation refresh page by{" "}
                    <span className={classes.refreshLink} onClick={handleReloadPage}>
                        clicking here
                    </span>{" "}
                    or type F5.
                </Typography>
            </LocalizedStrict>
            <Divider className={classes.helpDivider} />
            <Stack direction="row" alignItems="center" gap=".75rem">
                <LocalizedStrict id="label-printer-dialog-need-help">
                    <Typography className={classes.helpLabel}>Need help?</Typography>
                </LocalizedStrict>
                <LocalizedStrict id="label-printer-dialog-printer-guide-link">
                    <ExternalLink href="#" className={classes.helpLink} underline="hover">
                        Printer guide
                    </ExternalLink>
                </LocalizedStrict>
                <Typography className={classes.helpLabel}>|</Typography>
                <LocalizedStrict id="label-printer-dialog-order-printer-link">
                    <ExternalLink href="#" className={classes.helpLink} underline="hover">
                        Order a Brother label printer
                    </ExternalLink>
                </LocalizedStrict>
            </Stack>
        </Stack>
    )
}

function PrinterNotFoundContent({ onClose }: Readonly<{ onClose: () => void }>) {
    const { classes } = useStyles()

    return (
        <Stack alignItems="center" gap="2rem" padding="1rem">
            <LocalizedStrict id="label-printer-dialog-not-found-message">
                <Typography className={classes.description}>
                    No printer was detected. Did you connect the label printer?
                </Typography>
            </LocalizedStrict>
            <LocalizedStrict id="label-printer-dialog-try-again-button">
                <StandardButton color="black" size="medium" fullWidth={false} onClick={onClose}>
                    Try again
                </StandardButton>
            </LocalizedStrict>
        </Stack>
    )
}

export function LabelPrinterExtensionDialog({ open, onClose, variant }: LabelPrinterExtensionDialogProps) {
    const { classes } = useStyles()

    const notInstalled = variant === LabelPrinterDialogVariant.NOT_INSTALLED
    const titleId = notInstalled ? "label-printer-dialog-not-installed-title" : "label-printer-dialog-not-found-title"
    const titleText = notInstalled ? "Label printer extension required" : "No printer detected"

    return (
        <StandardDialog
            open={open}
            dialogTitle={
                <LocalizedStrict id={titleId}>
                    <>{titleText}</>
                </LocalizedStrict>
            }
            onClose={onClose}
            maxWidthProp="sm"
            scroll="body"
        >
            {notInstalled ? <NotInstalledContent /> : <PrinterNotFoundContent onClose={onClose} />}
        </StandardDialog>
    )
}
